import React, { useState, useEffect } from "react";
import { collection, getDocs, setDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";

// Forecast tab of the P&L Manager (Owner only).
// Same data as the old My Tracker "Forecaster": collection "myTrackerForecast", one document with 9 number fields.
// Nothing is copied or renamed. The profit split is the same 10% / 10% / 80%.
const DEFAULT_ID = "forecast_main";
const GOSHALA_PCT = 0.1;
const MOTHER_PCT = 0.1;
const INCOME = [
  ["platformSubscription", "Platform Subscription Revenue"],
  ["licensingIncome", "Licensing Income (Patent)"],
  ["consulting", "Consulting / Services"],
  ["otherIncome", "Other Income"],
];
const COSTS = [
  ["serverInfra", "Server / Infrastructure"],
  ["legalCompliance", "Legal & Compliance"],
  ["salaries", "Salaries / Freelancers"],
  ["marketing", "Marketing"],
  ["otherExpenses", "Other Expenses"],
];
const ALL = [...INCOME, ...COSTS].map((x) => x[0]);
const inr = (n) => "\u20B9" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const C = {
  box: { background: "#1a1d2e", border: "1px solid #2a2e45", borderRadius: 12, padding: 16 },
  title: { fontWeight: 700, color: "#e8eaf6", marginBottom: 10 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 },
  lbl: { fontSize: 13, color: "#c8cce0" },
  input: { width: 130, background: "#0f111a", border: "1px solid #3a3f5c", borderRadius: 8, padding: "6px 10px", color: "#e8eaf6", fontSize: 14, textAlign: "right", fontFamily: "monospace" },
  total: { display: "flex", justifyContent: "space-between", borderTop: "1px solid #2a2e45", paddingTop: 10, marginTop: 6 },
  mono: (c) => ({ fontFamily: "monospace", fontWeight: 700, fontSize: 15, color: c }),
};

export default function PnlForecastTab() {
  const [docId, setDocId] = useState(DEFAULT_ID);
  const [vals, setVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "myTrackerForecast"));
        if (snap.docs.length > 0) {
          setDocId(snap.docs[0].id);
          const v = {};
          ALL.forEach((k) => { v[k] = String(Number(snap.docs[0].get(k)) || 0); });
          setVals(v);
        }
      } catch (e) { setMsg("Error loading the forecast: " + e.message); }
      setLoading(false);
    })();
  }, []);

  const n = (k) => Number(vals[k]) || 0;
  const gross = INCOME.reduce((s, [k]) => s + n(k), 0);
  const costs = COSTS.reduce((s, [k]) => s + n(k), 0);
  const net = gross - costs;

  const save = async () => {
    setSaving(true);
    try {
      const out = {};
      ALL.forEach((k) => { out[k] = n(k); });
      await setDoc(doc(db, "myTrackerForecast", docId), { ...out, lastUpdatedAt: serverTimestamp() }, { merge: true });
      setMsg("Forecast saved.");
    } catch (e) { setMsg("Save error: " + e.message); }
    setSaving(false);
  };

  const field = ([k, label]) => (
    <div key={k} style={C.row}>
      <span style={C.lbl}>{label}</span>
      <input style={C.input} type="number" value={vals[k] == null ? "0" : vals[k]} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} />
    </div>
  );

  if (loading) return <div style={{ color: "#7b82a0" }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize: 14, color: "#e8eaf6", marginBottom: 12 }}>
        <b>Forecast</b> - your plan for income and costs (moved here from My Tracker). These are planned numbers, not actual entries.
      </div>
      {msg && <div style={{ ...C.box, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#c8cce0" }}>{msg}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
        <div style={C.box}>
          <div style={C.title}>Income plan</div>
          {INCOME.map(field)}
          <div style={C.total}><span style={C.lbl}>Gross income</span><span style={C.mono("#3ecf8e")}>{inr(gross)}</span></div>
        </div>
        <div style={C.box}>
          <div style={C.title}>Cost plan</div>
          {COSTS.map(field)}
          <div style={C.total}><span style={C.lbl}>Total costs</span><span style={C.mono("#f87171")}>{inr(costs)}</span></div>
        </div>
      </div>
      <div style={{ ...C.box, marginBottom: 16 }}>
        <div style={C.title}>Profit split</div>
        <div style={C.row}><span style={C.lbl}>Net profit before split</span><span style={C.mono("#4f8ef7")}>{inr(net)}</span></div>
        <div style={C.row}><span style={C.lbl}>Goshala (10%)</span><span style={C.mono("#fbbf24")}>{inr(Math.max(0, net * GOSHALA_PCT))}</span></div>
        <div style={C.row}><span style={C.lbl}>Mother (10%)</span><span style={C.mono("#a78bfa")}>{inr(Math.max(0, net * MOTHER_PCT))}</span></div>
        <div style={C.row}><span style={C.lbl}>You & Spouse (80%)</span><span style={C.mono("#3ecf8e")}>{inr(Math.max(0, net * 0.8))}</span></div>
      </div>
      <button style={{ background: "#6c63ff", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", cursor: "pointer", fontWeight: 600, fontSize: 14 }} disabled={saving} onClick={save}>
        {saving ? "Saving..." : "Save forecast"}
      </button>
    </div>
  );
}
